import React, { useEffect, useRef, useState } from 'react';
import { Select } from 'antd';

/**
 * One pagination contract for every list table.
 *
 * Three separate defects were being fixed one screen at a time, so this
 * puts the answer in a single place and every table imports it:
 *
 * 1. `pageSize` written as a literal (`pageSize: 10`) is a CONTROLLED value
 *    to antd: picking 20 fires the change and then re-renders with 10, so
 *    the size changer visibly does nothing. It has to be state that the
 *    handler moves, which is what `useTablePagination` holds.
 *
 * 2. antd 5.8.2 hardcodes `showSearch: true` on the Select it renders in
 *    the pagination bar -- see the MiniSelect/MiddleSelect wrappers in
 *    `antd/es/pagination/Pagination.js`. rc-pagination itself passes
 *    `showSearch: false`; antd overrides it. The object form of
 *    `showSizeChanger` that would turn it back off only arrived in antd
 *    5.25. The supported escape hatch in 5.8.2 is `selectComponentClass`:
 *    Pagination uses `selectComponentClass || (isSmall ? MiniSelect : ...)`,
 *    and Table forwards every key of its `pagination` object to Pagination,
 *    so passing our own class wins without touching node_modules.
 *
 * 3. Screens disagreed on the default (6, 10, or antd's own 10) and on the
 *    option list. Ten rows, everywhere.
 */

/**
 * Height for a list table's scrolling body.
 *
 * `CommonTableList` has always passed `scroll={{ y: window.innerHeight - 322 }}`,
 * and that is why the product and good-receive screens felt right while the
 * report tabs did not: bounding the BODY is what stops the page growing when
 * someone asks for 100 rows. A sticky pagination bar keeps the bar in view but
 * the page still runs on for thousands of pixels, which is what the user was
 * describing as "ไหลลงไปข้างล่าง".
 *
 * The floor is a ROW COUNT, not a pixel guess. A viewport-only formula gave a
 * laptop about five rows -- "5 รายการสั้นไป" -- because it subtracted a fixed
 * allowance from a short screen. Ten rows is also the default page size, so at
 * the default the body shows a whole page and does not scroll at all; the bound
 * only bites once someone asks for more. A tall monitor still gets more than
 * ten, because the viewport term wins there.
 *
 * `chrome` is the space above and below the table on that screen -- page
 * header, tab bar, KPI cards, section header, the pagination bar itself.
 * Deliberately a viewport-derived constant rather than a live measurement of
 * the table's own top: a table that starts below the fold measures almost no
 * room at mount and would collapse to the minimum.
 */

/** Measured in the running app: a default-size row is 69px, `size="small"` 47px. */
export const ROW_HEIGHT = 69;
export const ROW_HEIGHT_SMALL = 47;
export const MIN_VISIBLE_ROWS = 10;

export const tableScrollY = (chrome = 420, rowHeight: number = ROW_HEIGHT): number =>
    Math.max(MIN_VISIBLE_ROWS * rowHeight, Math.round(window.innerHeight - chrome));

/** Same floor of ten rows, for a table rendered with `size="small"`. */
export const smallTableScrollY = (chrome = 420): number => tableScrollY(chrome, ROW_HEIGHT_SMALL);

/**
 * Ten rows when the row height is not known in advance.
 *
 * Row height is not one number: 47px on a `size="small"` table, 69px on a
 * report row, 91px on a product row with its image and two-line cells. A
 * single constant gives ten rows on one screen and seven and a half on
 * another. `CommonTableList` renders every list screen in the app, so it
 * measures its own rows instead of guessing.
 *
 * Measuring only once is not enough either: the first paint happens before
 * the data lands, and rows grow afterwards -- a one-shot measurement locked
 * in 55px on a screen whose rows settled at 69px. So it re-measures and
 * converges, with three guards against thrashing:
 *
 *  - the MEDIAN row, not the tallest, so one wrapped cell does not stretch
 *    the body by a third;
 *  - a 4px tolerance, since changing the body height can add or remove its
 *    scrollbar and re-wrap a cell by a pixel or two;
 *  - a hard cap on how many times it will adjust, so no arrangement of
 *    content can loop.
 */
const MAX_MEASURE_PASSES = 4;

export const useMeasuredRowScrollY = (chrome: number, fallbackRowHeight: number = ROW_HEIGHT) => {
    const ref = useRef<HTMLDivElement>(null);
    const passes = useRef(0);
    const [rowHeight, setRowHeight] = useState<number | null>(null);

    useEffect(() => {
        if (passes.current >= MAX_MEASURE_PASSES) return;
        const rows = ref.current?.querySelectorAll('.ant-table-tbody tr.ant-table-row');
        if (!rows || rows.length === 0) return;
        const heights = Array.from(rows)
            .map((row) => Math.round(row.getBoundingClientRect().height))
            // jsdom, and a row not yet painted, both report 0.
            .filter((h) => h > 16)
            .sort((a, b) => a - b);
        if (heights.length === 0) return;
        const median = heights[Math.floor(heights.length / 2)];
        if (rowHeight !== null && Math.abs(median - rowHeight) <= 4) return;
        passes.current += 1;
        setRowHeight(median);
    });

    return { ref, y: tableScrollY(chrome, rowHeight ?? fallbackRowHeight) };
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

/** The pagination size changer, with antd's forced search box turned off. */
const PageSizeSelect: any = (props: any) => <Select {...props} showSearch={false} />;
PageSizeSelect.Option = Select.Option;

export { PageSizeSelect };

/**
 * Build the config from a caller-owned `pageSize` state pair. Use this when
 * a screen already holds the state; otherwise use `useTablePagination`.
 */
export const tablePagination = (
    pageSize: number,
    setPageSize: (size: number) => void,
    extra: Record<string, any> = {}
) => ({
    pageSize,
    onShowSizeChange: (_current: number, size: number) => setPageSize(size),
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    selectComponentClass: PageSizeSelect,
    locale: { items_per_page: '/ หน้า' },
    showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ`,
    responsive: true,
    size: 'small' as const,
    ...extra,
});

/** Drop-in for `pagination={...}` on any client-side list table. */
export const useTablePagination = (
    initialPageSize: number = DEFAULT_PAGE_SIZE,
    extra: Record<string, any> = {}
) => {
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    return tablePagination(pageSize, setPageSize, extra);
};

export default useTablePagination;
